import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { ApiService } from '../../../../SGRE-shared/services/api.service';
import { CartPageActions } from '../Actions';
import { GlobalService } from '../../../../SGRE-shared/services/global.service';
import { ErrorType } from '../../../../SGRE-config/error-config';
import { CartEffectsService } from './cart-effect.service';
import { CustomCart, CartProductsResponseType, UpdateQuantityAction, CheckOutResponseBody, CartsData, CartCouponData } from '../../models/custom-cart.model';
import { GlobalMessageType, LoggerService, tryNormalizeHttpError } from '@spartacus/core';
import { HttpResponse } from '@angular/common/http';
import { GlobalMessageService } from '@spartacus/core';
import { ActiveCartFacade, MultiCartFacade } from '@spartacus/cart/base/root';
import { Store } from '@ngrx/store';

@Injectable()
export class CartPageEffects {

  protected logger = inject(LoggerService);

  constructor(
    private actions$: Actions,
    private apiService: ApiService,
    private globalService: GlobalService,
    private cartEffectsService: CartEffectsService,
    private globalMessageService: GlobalMessageService,
    private multiCartFacade: MultiCartFacade,
    private activeCartService: ActiveCartFacade,
    private store: Store,
  ) { }

  //APi call: To load the total Products for the Cart Page
  loadCartProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CartPageActions.loadProductsForCart),
      switchMap((action: { type: string; cartCode?: string }) => {
        if (!action.cartCode) {
          return of(CartPageActions.loadCartProductsFailure({
            error: { message: 'No cart code provided' } as any
          }));
        }
        this.globalService.loadingSubject.next(true);
        return this.apiService.getCartsList(action.cartCode).pipe(
          map((data: unknown) => {
            const errorProducts = [];
            const typedData = data as CartProductsResponseType;
            const cartsTableData = this.cartEffectsService.mapAndConfigureCartData(typedData);
            // if(typedData["couponValidationErrorMessage"]){
            //   this.globalMessageService.add(typedData["couponValidationErrorMessage"], GlobalMessageType.MSG_TYPE_WARNING);
            // }else{
            //   this.globalService.clearMessagesOnDestroy(ErrorType.WARNING);
            // }        

            typedData.entries.forEach((item: CartsData) => {

              if (item.message == 6) {
                this.globalMessageService.remove(GlobalMessageType.MSG_TYPE_WARNING);
                errorProducts.push(item);
              }
            });
            if (!errorProducts.length) {
              this.checkMessage(typedData);
            }
            return CartPageActions.loadCartProductsSuccess({ cartProductsData: cartsTableData });
          }),
          catchError(error => of(CartPageActions.loadCartProductsFailure({ error: tryNormalizeHttpError(error, this.logger) }))),
          finalize(() => this.globalService.loadingSubject.next(false))
        );
      })
    )
  );

  loadCustomerCoupon$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CartPageActions.loadCustomerCoupon),
      switchMap(() => {
        this.globalService.loadingSubject.next(true);
        return this.apiService.getCustomerCouponList().pipe(
          map((cartCouponData: CartCouponData[]) => {
            const mappedCoupons = cartCouponData.map(coupon => ({
              ...coupon,
              code: coupon.couponId
            }));
            return CartPageActions.loadCustomerCouponSuccess({ cartCouponData: mappedCoupons });
          }),
          catchError(error => of(CartPageActions.loadCustomerCouponFailure({ error: tryNormalizeHttpError(error, this.logger) }))),
          finalize(() => this.globalService.loadingSubject.next(false))
        );
      })
    )
  );

  applyCoupon$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CartPageActions.applyCoupon),
      switchMap(({ cartId, voucherId }) => {
        this.globalService.loadingSubject.next(true);
        return this.apiService.applyCouponToCart(cartId, voucherId).pipe(
          switchMap(() => this.apiService.getCartsList(cartId)),
          map((cartData: CartProductsResponseType) => {
            const cartsTableData = this.cartEffectsService.mapAndConfigureCartData(cartData);
            this.store.dispatch(CartPageActions.loadCartProductsSuccess({ cartProductsData: cartsTableData }));
            this.checkMessage(cartData);
            const voucherApplied = cartData.appliedVouchers?.some(v => v.code === voucherId);
            if (voucherApplied) {
              return CartPageActions.applyCouponSuccess({ voucherId });
            }
            return CartPageActions.applyCouponFailure({
              error: { message: cartData.couponValidationErrorMessage || 'Coupon could not be applied' }
            });
          }),
          catchError(error => {
            const errorMessage = error?.error?.couponValidationErrorMessage || 'Failed to apply coupon';
            return of(CartPageActions.applyCouponFailure({ error: { message: errorMessage } }));
          }),
          finalize(() => this.globalService.loadingSubject.next(false))
        );
      })
    )
  );

  removeCoupon$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CartPageActions.removeCoupon),
      switchMap(({ cartId, voucherId }) => {
        this.globalService.loadingSubject.next(true);
        return this.apiService.removeCouponFromCart(cartId, voucherId).pipe(
          switchMap(() => this.apiService.getCartsList(cartId)),
          map((cartData: CartProductsResponseType) => {
            this.checkMessage(cartData);
            const cartsTableData = this.cartEffectsService.mapAndConfigureCartData(cartData);
            this.store.dispatch(CartPageActions.loadCartProductsSuccess({ cartProductsData: cartsTableData }));
            const voucherApplied = cartData.appliedVouchers?.some(v => v.code === voucherId);
            if (!voucherApplied) {
              return CartPageActions.removeCouponSuccess({ voucherId });
            }
            return CartPageActions.removeCouponFailure({
              error: { message: cartData.couponValidationErrorMessage || 'Coupon could not be applied' }
            });
          }),
          catchError(error => {
            const errorMessage = error?.error?.couponValidationErrorMessage || 'Failed to remove coupon';
            return of(CartPageActions.removeCouponFailure({ error: { message: errorMessage } }));
          }),
          finalize(() => this.globalService.loadingSubject.next(false))
        );
      })
    )
  );

  // checkForPriceUpdate(apiResponse: CartProductsResponseType, cartId: string) {
  //   combineLatest([
  //     this.activeCartService.getActiveCartId(),
  //     this.multiCartFacade.getCart(cartId)
  //   ])
  //     .pipe(take(1))
  //     .subscribe(([activeCartId, cartData]: [string, Cart]) => {
  //       if (activeCartId === cartData.code && cartData["priceFetchErrorEntries"].length > 0) {
  //         if (cartData['priceFetchErrorEntries'].includes(',')) {
  //           this.displayPriceErrorMessageProducts(cartData['priceFetchErrorEntries']);
  //         } else {
  //           this.displayPriceErrorMessage(cartData['priceFetchErrorEntries']);
  //         } 
  //       } else if (apiResponse.priceFetchErrorEntries?.length > 0) {
  //         if (cartData['priceFetchErrorEntries'].includes(',')) {
  //           this.displayPriceErrorMessageProducts(cartData['priceFetchErrorEntries']);
  //         } else {
  //           this.displayPriceErrorMessage(cartData['priceFetchErrorEntries']);
  //         }
  //       } else if (!apiResponse.priceFetchErrorEntries || apiResponse.priceFetchErrorEntries.length === 0) {
  //         this.globalService.clearMessagesOnDestroy(ErrorType.WARNING);
  //       }

  //       // Handle price updates
  //       if (activeCartId === cartData.code && cartData["priceUpdatedEntries"].length > 0) {
  //         if (cartData['priceUpdatedEntries'].includes(',')) {
  //           this.displayPriceUpdatedMessageProducts(cartData['priceUpdatedEntries']);
  //         } else {
  //           this.displayPriceUpdatedMessage(cartData['priceUpdatedEntries']);
  //         }
  //       } else if (apiResponse.priceUpdatedEntries?.length > 0) {
  //         if (cartData['priceUpdatedEntries'].includes(',')) {
  //           this.displayPriceUpdatedMessageProducts(cartData['priceUpdatedEntries']);
  //         } else {
  //           this.displayPriceUpdatedMessage(cartData['priceUpdatedEntries']);
  //         }
  //       } else if (!apiResponse.priceUpdatedEntries && (!apiResponse.priceFetchErrorEntries || apiResponse.priceFetchErrorEntries.length === 0)) {
  //         this.globalService.clearMessagesOnDestroy(ErrorType.WARNING);
  //       }
  //     });
  // }

  checkMessage(data: CartProductsResponseType) {
    if (!data) {
      return;
    }

    this.globalMessageService.remove(GlobalMessageType.MSG_TYPE_WARNING);

    this.handlePriceFetchErrors(data);
    this.handlePriceUpdates(data);
    this.handleCouponValidationMessage(data);
    this.clearMessagesIfNoIssues(data);
  }

  private handlePriceFetchErrors(data: CartProductsResponseType): void {
    const errorCount = data.priceFetchErrorEntries?.length || 0;

    if (errorCount > 0) {
      const errorMessage = this.formatEntriesToString(data.priceFetchErrorEntries);

      if (errorCount === 1) {
        this.displayPriceErrorMessage(errorMessage);
      } else {
        this.displayPriceErrorMessageProducts(errorMessage);
      }
    }
  }

  private handlePriceUpdates(data: CartProductsResponseType): void {
    const updateCount = data.priceUpdatedEntries?.length || 0;

    if (updateCount > 0) {
      const updatedMessage = this.formatEntriesToString(data.priceUpdatedEntries);

      if (updateCount === 1) {
        this.displayPriceUpdatedMessage(updatedMessage);
      } else {
        this.displayPriceUpdatedMessageProducts(updatedMessage);
      }

      this.apiService.clearNotification(data["code"]).subscribe();
    }
  }

  private handleCouponValidationMessage(data: CartProductsResponseType): void {
    if (data.couponValidationErrorMessage) {
      this.displayCouponValidationMessage(data.couponValidationErrorMessage);
    }
  }

  private clearMessagesIfNoIssues(data: CartProductsResponseType): void {
    const hasNoUpdates = (data.priceUpdatedEntries?.length || 0) === 0;
    const hasNoErrors = (data.priceFetchErrorEntries?.length || 0) === 0;
    const hasNoCoupons = (data.couponValidationErrorMessage?.length || 0) === 0;

    if (hasNoUpdates && hasNoErrors && hasNoCoupons) {
      this.globalMessageService.remove(GlobalMessageType.MSG_TYPE_WARNING);
    }
  }

  private formatEntriesToString(entries: any): string {
    return Array.isArray(entries) ? entries.join(', ') : entries;
  }

  displayCouponValidationMessage(message: string): void {
    this.globalMessageService.add(message, GlobalMessageType.MSG_TYPE_WARNING);
  }

  displayPriceUpdatedMessage(message: string): void {
    this.globalMessageService.add(
      `Price has been updated for the product [${message}] `,
      GlobalMessageType.MSG_TYPE_WARNING
    );
  }

  displayPriceUpdatedMessageProducts(message: string): void {
    this.globalMessageService.add(
      `Prices have been updated for the products [${message}] `,
      GlobalMessageType.MSG_TYPE_WARNING
    );
  }

  displayPriceErrorMessage(message: string): void {
    this.globalMessageService.add(
      ` There was an error while loading the price for [${message}]. Please delete the respective product from the cart to proceed. `,
      GlobalMessageType.MSG_TYPE_WARNING
    );
  }

  displayPriceErrorMessageProducts(message: string): void {
    this.globalMessageService.add(
      ` There was an error while loading the prices for [${message}]. Please delete the respective products from the cart to proceed. `,
      GlobalMessageType.MSG_TYPE_WARNING
    );
  }

  //APi call: To Update product quantity in the cart
  updateQtyOfProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CartPageActions.updateProductQty),
      switchMap((action: UpdateQuantityAction) => {
        this.globalService.loadingSubject.next(true);
        return this.apiService.updateProductsInCart(action.cartId, action.entryNumber, { quantity: action.quantity }).pipe(
          map((cart: CustomCart[]) => {
            // console.log('%cUpdated Products quantity in the Cart API call successful from Effects', 'color: red; font-weight: bold;', cart);
            return CartPageActions.updateProductQtySuccess({ updatedCartProducts: cart });
          }),
          catchError(error => {
            this.globalService.loadingSubject.next(false);
            return of(CartPageActions.updateProductQtyFailure({ error: tryNormalizeHttpError(error, this.logger) }));
          })
        );
      })
    )
  );

  //Api call: From cart page to the checkout page
  postToCheckout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CartPageActions.postToCheckout),
      switchMap(({ cartId }) => {
        this.globalService.loadingSubject.next(true);
        const paramsObject = {
          fields: "FULL"
        }
        return this.apiService.postToCheckout(cartId, paramsObject).pipe(
          map((response: HttpResponse<CheckOutResponseBody>) => {
            // console.log('%cPost to Checkout API call successful from Effects', 'color: green; font-weight: bold;', response);
            return CartPageActions.postToCheckoutSuccess({ response: response.body });
          }),
          tap(() => this.globalService.clearMessagesOnDestroy(ErrorType.WARNING)),
          catchError((error) => {
            // console.error('%cPost to Checkout API call failed', 'color: red; font-weight: bold;', error);
            return of(CartPageActions.postToCheckoutFailure({ error: tryNormalizeHttpError(error, this.logger) }));
          }),
          finalize(() => {
            this.globalService.loadingSubject.next(false);
          })
        );
      })
    )
  );
}